import { CommonModule, isPlatformBrowser } from '@angular/common';
import type { AfterViewInit, ElementRef } from '@angular/core';
import {
	ChangeDetectorRef,
	Component,
	computed,
	ContentChild,
	inject,
	input,
	PLATFORM_ID,
	signal,
	ViewChild,
} from '@angular/core';
import type { Params } from '@angular/router';
import { RouterModule } from '@angular/router';

import { LetDirective } from '@ngrx/component';
import { TranslatePipe } from '@ngx-translate/core';
import { MarkdownModule } from 'ngx-markdown';
import type { Observable } from 'rxjs';

import { IconListComponent } from '@valk-nx/components/ui-icon-list/src/lib/icon-list.component';
import type {
	Icon,
	IconListColor,
} from '@valk-nx/components/ui-icon-list/src/lib/icon-list.interface';
import { LinkListComponent } from '@valk-nx/components/ui-link-list/src/lib/link-list.component';
import { PricingComponent } from '@valk-nx/components/ui-pricing/src/lib/pricing';
import { SocialProofComponent } from '@valk-nx/components/ui-social-proof/src/lib/social-proof';
import { TagComponent } from '@valk-nx/components/ui-tag/src/lib/tag';
import { SmartLinkComponent } from '@valk-nx/core/lib/components/smart-link/smart-link.component';
import type { LinkTarget } from '@valk-nx/core/lib/core';
import { cn } from '@valk-nx/core/lib/core';
import type { HeadingType } from '@valk-nx/core/lib/directives/heading/heading.type';
import type { SocialProofInterface } from '@valk-nx/core/lib/interfaces/hotels.interface';
import type { PhotoInterface } from '@valk-nx/core/lib/interfaces/image.interface';
import type { PriceInterface } from '@valk-nx/core/lib/interfaces/price.interface';
import type { PricingTooltip } from '@valk-nx/helpers/lib/pricing-tooltip/pricing-tooltip.interface';
import { SVG_ICON_IMPORTS } from '@valk-nx/helpers/lib/svg-unique-ids/svg-unique-ids.directive';
import { ViewPortService } from '@valk-nx/services/viewport/src/lib/viewport.service';

import { PersuadeCardHeaderComponent } from './sub-components/header/persuade-card-header.component';
import { PersuadeCardMediaComponent } from './sub-components/media/persuade-card-media.component';
import { FooterContentDirective } from './persuade-card.directive';
import type { LinkType, PersuadeCardLinkList } from './persuade-card.interface';

@Component({
	host: { ngSkipHydration: 'true' },
	selector: 'vp-persuade-card',
	templateUrl: './persuade-card.component.html',
	imports: [
		SVG_ICON_IMPORTS,
		CommonModule,
		IconListComponent,
		LetDirective,
		LinkListComponent,
		MarkdownModule,
		PersuadeCardHeaderComponent,
		PersuadeCardMediaComponent,
		RouterModule,
		SmartLinkComponent,
		PricingComponent,
		SocialProofComponent,
		TagComponent,
		TranslatePipe,
	],
})
export class PersuadeCardComponent implements AfterViewInit {
	private readonly platformId = inject(PLATFORM_ID);
	readonly cd = inject(ChangeDetectorRef);
	private readonly viewport = inject(ViewPortService);

	// Image Inputs
	images = input<PhotoInterface[] | undefined>(undefined);
	imageVariant = input<'mini-carousel' | 'single' | 'grid'>('single');
	imageRibbon = input<string | undefined>(undefined);
	isTransparentImage = input<boolean>(false);

	// Header Inputs
	overline = input<string | undefined>(undefined);
	title = input.required<string>();
	headingType = input<HeadingType>('h3');

	// Body Inputs
	socialProof = input<SocialProofInterface | undefined>(undefined);
	content = input<string | undefined>(undefined);
	tags = input<string[] | undefined>(undefined);
	iconList = input<Icon[]>([]);
	iconColor = input<IconListColor>('text-dark-primary');
	isHorizontalIconList = input<boolean>(true);

	// Pricing Inputs
	price = input<PriceInterface | undefined>(undefined);
	pricingToolTip = input<PricingTooltip | undefined>(undefined);

	// CTA Inputs
	cardUrl = input<string | undefined>(undefined);
	cardUrlQueryParams = input<Params>();
	ctaUrlQueryParams = input<Params>();
	cardLinkType = input<LinkType>('story');
	ctaUrl = input<string | undefined>(undefined);
	ctaForceReload = input<boolean>(false);
	ctaTarget = input<LinkTarget | undefined>(undefined);
	ctaText = input<string>('global.book-directly');
	ctaType = input<'btn-primary' | 'btn-secondary'>('btn-primary');
	ctaVariant = input<'' | 'btn-outline' | 'btn-text'>('');
	ctaLinkType = input<LinkType>('story');
	secondaryCtaTarget = input<LinkTarget | undefined>(undefined);
	secondaryCtaText = input<string | undefined>(undefined);
	secondaryCtaLinkType = input<LinkType>('story');

	// Link List Inputs
	linkListConfig = input<PersuadeCardLinkList | undefined>();

	// Layout Inputs
	reversedCtaColumn = input<boolean>(false);
	useVerticalCard = input<boolean>(false);
	showReadMore = input<boolean>(false);
	readMoreText = input<string>('global.show-more');

	showFooterArea = computed(() => {
		return (
			!!this.price() ||
			!!this.ctaUrl() ||
			this.linkListHasItems() ||
			this.hasFooterContent()
		);
	});

	pricingConditionByType = computed(() => {
		const t = this.price()?.type;
		if (t === 'perPerson') return 'global.price-per-person';
		if (t === 'perNight') return 'global.price-per-night';
		return 'global.price';
	});

	ctaPrimaryClasses = computed(
		() => `${this.ctaType()} ${this.ctaVariant()}`,
	);

	secondaryCtaVariant = computed(() =>
		this.ctaVariant() === ''
			? `${this.ctaType()} btn-outline`
			: `${this.ctaType()} btn-text`,
	);

	linkListHasItems = computed(
		() => (this.linkListConfig()?.items?.length ?? 0) > 0,
	);

	readonly isMobile$: Observable<boolean>;
	readonly isTablet$: Observable<boolean>;
	height = 0;

	@ViewChild('persuadeRef') persuadeRef!: ElementRef;

	cn = cn;

	// returns a boolean<true> when detected that <ng-content footerContent> is being used
	private readonly _hasFooterContent = signal(false);
	readonly hasFooterContent = computed(() => this._hasFooterContent());

	@ContentChild(FooterContentDirective)
	set footerSlot(dir: FooterContentDirective | undefined) {
		this._hasFooterContent.set(!!dir);
	}

	constructor() {
		this.isMobile$ = this.viewport.isMobile$;
		this.isTablet$ = this.viewport.isSmallTablet$;
	}

	ngAfterViewInit(): void {
		if (isPlatformBrowser(this.platformId)) {
			setTimeout(() => {
				this.height = this.persuadeRef.nativeElement.offsetHeight;
				this.cd.detectChanges();
			});
		}
	}
}
